/*
🚨 Safety Intelligence Widget
Ana dashboard için kompakt intelligence widget'ı
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Badge,
  Progress,
  Button,
  Space,
  Typography,
  List,
  Avatar,
  Tag,
  Tooltip,
  Spin,
  Alert
} from 'antd';
import {
  SecurityScanOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text, Title } = Typography;

// Mock data - gerçek API'den gelecek
const mockStats = {
  totalThreats: 247,
  activeAlerts: 12,
  processedRecords: 15847,
  systemHealth: 96
};

const mockRecentThreats = [
  {
    id: '1',
    type: 'harassment_call',
    location: 'Kadıköy',
    severity: 'high',
    timestamp: '14:23'
  },
  {
    id: '2',
    type: 'missing_person',
    location: 'Beyoğlu',
    severity: 'critical',
    timestamp: '13:45'
  },
  {
    id: '3',
    type: 'crime_pattern',
    location: 'Şişli',
    severity: 'medium',
    timestamp: '12:15'
  }
];

const mockDataSources = [
  { name: 'EGM Database', health: 98, status: 'active' },
  { name: 'BTK Telecom', health: 95, status: 'active' },
  { name: 'Istanbul Municipality', health: 78, status: 'warning' },
  { name: 'Missing Persons', health: 99, status: 'active' }
];

interface SafetyIntelligenceWidgetProps {
  height?: number;
}

const SafetyIntelligenceWidget: React.FC<SafetyIntelligenceWidgetProps> = ({ height = 400 }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(mockStats);
  const navigate = useNavigate();

  useEffect(() => {
    // Load initial data
    loadWidgetData();
  }, []);

  const loadWidgetData = async () => {
    setLoading(true);
    try {
      // Gerçek API çağrıları burada yapılacak
      await new Promise(resolve => setTimeout(resolve, 1000));
      // setStats(response.data);
    } catch (error) {
      console.error('Widget data load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#ff7a45';
      case 'medium': return '#ffa940';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getSeverityText = (severity: string) => {
    switch (severity) {
      case 'critical': return 'KRİTİK';
      case 'high': return 'YÜKSEK';
      case 'medium': return 'ORTA';
      case 'low': return 'DÜŞÜK';
      default: return 'BİLİNMEYEN';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <DatabaseOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  return (
    <Card
      title={
        <Space>
          <SecurityScanOutlined style={{ color: '#1890ff' }} />
          <span>🚨 Safety Intelligence</span>
        </Space>
      }
      extra={
        <Button
          type="link"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate('/safety-intelligence')}
        >
          Detayları Görüntüle
        </Button>
      }
      style={{ height }}
    >
      <Spin spinning={loading}>
        {/* Quick Stats */}
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                value={stats.totalThreats}
                prefix={<SafetyCertificateOutlined style={{ color: '#ff4d4f', fontSize: '14px' }} />}
                valueStyle={{ fontSize: '16px', color: '#ff4d4f' }}
              />
              <Text type="secondary" style={{ fontSize: '11px' }}>Tehdit</Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                value={stats.activeAlerts}
                prefix={<AlertOutlined style={{ color: '#fa8c16', fontSize: '14px' }} />}
                valueStyle={{ fontSize: '16px', color: '#fa8c16' }}
              />
              <Text type="secondary" style={{ fontSize: '11px' }}>Uyarı</Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                value={stats.processedRecords}
                prefix={<DatabaseOutlined style={{ color: '#1890ff', fontSize: '14px' }} />}
                valueStyle={{ fontSize: '16px', color: '#1890ff' }}
              />
              <Text type="secondary" style={{ fontSize: '11px' }}>Kayıt</Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                value={stats.systemHealth}
                suffix="%"
                prefix={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '14px' }} />}
                valueStyle={{ fontSize: '16px', color: '#52c41a' }}
              />
              <Text type="secondary" style={{ fontSize: '11px' }}>Sağlık</Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Recent Threats */}
          <Col span={14}>
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                Son Tehditler
              </Title>
              <List
                size="small"
                dataSource={mockRecentThreats}
                renderItem={(threat) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <Avatar 
                            size="small"
                            style={{ 
                              backgroundColor: getSeverityColor(threat.severity),
                              fontSize: '10px'
                            }}
                          >
                            !
                          </Avatar>
                          <div>
                            <Text style={{ fontSize: '12px' }}>
                              <EnvironmentOutlined style={{ marginRight: 2 }} />
                              {threat.location}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                              <ClockCircleOutlined style={{ marginRight: 2 }} />
                              {threat.timestamp}
                            </Text>
                          </div>
                        </Space>
                        <Tag 
                          color={getSeverityColor(threat.severity)}
                          style={{ fontSize: '10px', padding: '0 4px', margin: 0 }}
                        >
                          {getSeverityText(threat.severity)}
                        </Tag>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </Col>

          {/* Data Sources Health */}
          <Col span={10}>
            <div>
              <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                <DatabaseOutlined style={{ color: '#1890ff', marginRight: 4 }} />
                Veri Kaynakları
              </Title>
              <List
                size="small"
                dataSource={mockDataSources}
                renderItem={(source) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <Space>
                          {getStatusIcon(source.status)}
                          <Text style={{ fontSize: '11px' }}>{source.name}</Text>
                        </Space>
                        <Text style={{ fontSize: '10px', color: '#666' }}>
                          {source.health}%
                        </Text>
                      </div>
                      <Progress 
                        percent={source.health} 
                        size="small" 
                        showInfo={false}
                        strokeColor={source.health > 90 ? '#52c41a' : source.health > 70 ? '#fa8c16' : '#ff4d4f'}
                        style={{ fontSize: '2px' }}
                      />
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </Col>
        </Row>

        {/* Quick Actions */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <Space size="small">
            <Button 
              size="small" 
              type="primary" 
              ghost
              icon={<EyeOutlined />}
              onClick={() => navigate('/safety-intelligence')}
            >
              Analiz
            </Button>
            <Button 
              size="small"
              icon={<SecurityScanOutlined />}
              onClick={() => navigate('/safety-intelligence')}
            >
              Dashboard
            </Button>
          </Space>
        </div>
      </Spin>
    </Card>
  );
};

export default SafetyIntelligenceWidget;
